import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button, Space, Typography, Row, Col, Input, Tag, Pagination } from 'antd';
import { ShopOutlined, ReloadOutlined, FileExcelOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useShopManagement } from './useShopManagement';
import { getShopTableColumns } from './shopTableColumns';
import ShopEditModal from './ShopEditModal';
import ShopFileModal from './ShopFileModal';
import { SHOP_STATUS } from '../../helper';
import './AccountShop.css';

const { Title, Text } = Typography;

/**
 * Get display text for filter type
 * @param {string} filter - Filter type
 * @returns {string} Display text
 */
const getFilterDisplay = (filter) => {
  switch (filter) {
    case SHOP_STATUS.APPROVED:
      return 'อนุมัติแล้ว';
    case SHOP_STATUS.PENDING:
      return 'รออนุมัติ';
    case SHOP_STATUS.SUSPENDED:
      return 'ระงับการใช้งาน';
    default:
      return 'ทั้งหมด';
  }
};

/**
 * Account Shop Management Component
 * Handles approved, pending, and suspended shops based on filter prop
 */
function AccountShopManagement(props) {
  const { filter } = props;

  const {
    form,
    loading,
    modalLoading,
    userFiltered,
    useritem,
    files,
    fileselected,
    filtertext,
    isModalUserOpen,
    isModalFileOpen,
    currentPage,
    pageSize,
    setFiltertext,
    setUseritem,
    setCurrentPage,
    setPageSize,
    getUserList,
    manageUsers,
    handleSubmit,
    handleCategoryChange,
    readfile,
    selectFilename,
    downloadfile,
    downloadCSV,
    handleUploadDoc,
    closeUserModal,
    closeFileModal,
    app_hostname,
    fulfillmentConfig,
    setFulfillmentConfig,
    fulfillmentLoading,
  } = useShopManagement(props);

  const columns = getShopTableColumns({
    onReadFile: readfile,
    onManageUser: manageUsers,
  });

  const filterDisplay = getFilterDisplay(filter);

  const renderMobileCards = () => {
    return (
      <div className='accountshop-mobile-view'>
        <div className='shop-card-container'>
          {userFiltered
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((record) => (
              <div className='shop-card' key={record.shop_id}>
                <div className='shop-card-left'>
                  <div className='shop-card-header'>
                    <span className='shop-card-title'>
                      {record.shop_name || record.shop_username}
                    </span>
                    <Tag
                      color={record.shopdocument === 'submited' ? 'success' : 'default'}
                      style={{ margin: 0, borderRadius: '10px' }}
                    >
                      {record.shopdocument || 'pending'}
                    </Tag>
                  </div>
                  <div className='shop-card-subtitle'>
                    {record.shop_name} {record.shop_province ? `, ${record.shop_province}` : ''}
                  </div>
                  <div className='shop-card-time' style={{ fontSize: '11px', color: '#8c8c8c' }}>
                    สมัครเมื่อ:{' '}
                    {record.createdate ? dayjs(record.createdate).format('DD/MM/YYYY HH:mm') : '-'}
                  </div>
                  <div className='shop-card-id'>{record.shop_id}</div>
                </div>

                <div className='shop-card-right'>
                  <Button
                    className='btn-outline-blue'
                    size='small'
                    block
                    onClick={() => readfile(record)}
                  >
                    ดูไฟล์
                  </Button>
                  <Button
                    className='btn-outline-orange'
                    size='small'
                    block
                    onClick={() => manageUsers(record)}
                  >
                    แก้ไขข้อมูล
                  </Button>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className='page-wrapper'>
      <div className='page-card'>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          <div className='page-header-actions'>
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <ShopOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
                {filterDisplay}
              </Title>
              <Text type='secondary'>ระบบจัดการและอัปเดตข้อมูลบัญชีร้านค้าในระบบ</Text>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={getUserList} loading={loading}>
                รีเฟรช
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={downloadCSV}>
                ส่งออกไฟล์ CSV
              </Button>
            </Space>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} lg={8}>
              <Text strong>ค้นหาร้านค้า:</Text>
              <Input
                className='mobile-search-input'
                placeholder='รหัสร้านค้า, ชื่อร้าน, อีเมล, จังหวัด, เบอร์โทร'
                value={filtertext}
                onChange={(e) => setFiltertext(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                style={{ width: '100%', marginTop: '8px' }}
              />
            </Col>
          </Row>

          <div>
            <Title level={5} style={{ marginBottom: '16px' }}>
              รายการ{filterDisplay} <span>({userFiltered.length} บัญชี)</span>
            </Title>

            <div className='accountshop-desktop-view'>
              <Table
                columns={columns}
                dataSource={userFiltered}
                rowKey='shop_id'
                loading={loading}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  showTotal: (total) => `รวม ${total} บัญชี`,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: (page, newPageSize) => {
                    setCurrentPage(page);
                    setPageSize(newPageSize);
                  },
                }}
                size='small'
                scroll={{ x: true }}
              />
            </div>

            {renderMobileCards()}

            <div className='accountshop-mobile-view'>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={userFiltered.length}
                  onChange={(page, newPageSize) => {
                    setCurrentPage(page);
                    setPageSize(newPageSize);
                  }}
                  showSizeChanger={false}
                  size='small'
                />
              </div>
            </div>
          </div>
        </Space>
      </div>

      <ShopEditModal
        open={isModalUserOpen}
        useritem={useritem}
        form={form}
        loading={modalLoading}
        app_hostname={app_hostname}
        token={props.permitdata?.token || ''}
        onCancel={closeUserModal}
        onSubmit={handleSubmit}
        onUserItemChange={(changes) => {
          setUseritem((prev) => ({ ...prev, ...changes }));
        }}
        onCategoryChange={handleCategoryChange}
        fulfillmentConfig={fulfillmentConfig}
        fulfillmentLoading={fulfillmentLoading}
        onFulfillmentChange={setFulfillmentConfig}
      />

      <ShopFileModal
        open={isModalFileOpen}
        useritem={useritem}
        files={files}
        fileselected={fileselected}
        app_hostname={app_hostname}
        onCancel={closeFileModal}
        onSelectFile={selectFilename}
        onDownload={downloadfile}
        onUpload={handleUploadDoc}
      />
    </div>
  );
}

AccountShopManagement.propTypes = {
  filter: PropTypes.oneOf([SHOP_STATUS.APPROVED, SHOP_STATUS.PENDING, SHOP_STATUS.SUSPENDED])
    .isRequired,
  permitdata: PropTypes.shape({
    token: PropTypes.string.isRequired,
  }).isRequired,
  app_hostname: PropTypes.string.isRequired,
};

export default AccountShopManagement;
